import React, { useEffect, useRef, useState } from 'react';
import { confirmBrainApply, conversationForSend, streamBrainReply } from '../../utils/api/streamBrain';
import { useBrainCanvas } from '../../context/BrainCanvasContext';
import { useBrainResize } from './useBrainResize';
import './BrainPanel.css';

const STUB_MEMORIES = [
  { id: 'm1', type: 'preference', text: 'Prefers flow view for campaign planning', meta: 'Stub · preference' },
  { id: 'm2', type: 'fact', text: 'Q3 launch campaign is the active focus', meta: 'Stub · fact' },
  { id: 'm3', type: 'event', text: 'Linked “Onboarding playbook” to Kickoff mission', meta: 'Stub · event' },
];

/**
 * Docked Brain panel — talks to the streamed brain door.
 * Replies stream from the brain. Close the panel and the thread dies.
 */
const BrainPanel = ({
  isOpen = false,
  onClose,
  canViewMemory = false,
  onOpenMemory,
  workspaceId,
  campaignId,
}) => {
  const [draft, setDraft] = useState('');
  const [askReply, setAskReply] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const { applyPatch, applyClipboard, undo } = useBrainCanvas();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const {
    brainWidth,
    isResizing,
    panelRef,
    handleResizeStart,
    handleResizeReset,
  } = useBrainResize(isOpen);

  useEffect(() => {
    if (isOpen) return undefined;
    setMessages([]);
    setDraft('');
    setAskReply('');
    setBusy(false);
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isOpen]);

  if (!isOpen) return null;

  const sendText = async (raw) => {
    const text = String(raw || '').trim();
    if (!text || busy) return;

    const userId = `u-${Date.now()}`;
    const assistantId = `a-${Date.now()}`;
    const thread = conversationForSend(messages, text);
    setDraft('');
    setAskReply('');
    setBusy(true);
    setMessages((prev) => [
      ...prev.map((msg) => (msg.ask && !msg.askAnswered ? { ...msg, askAnswered: true } : msg)),
      { id: userId, role: 'user', text },
      { id: assistantId, role: 'assistant', text: '', pending: true },
    ]);

    try {
      await streamBrainReply({
        text,
        messages: thread,
        workspaceId,
        campaignId,
        onChunk: (soFar) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, text: soFar, pending: false } : msg
            )
          );
        },
        onChip: (label) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? {
                    ...msg,
                    pending: false,
                    chips: msg.chips?.includes(label) ? msg.chips : [...(msg.chips || []), label],
                  }
                : msg
            )
          );
        },
        onAsk: (question) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, pending: false, ask: question } : msg
            )
          );
        },
        onUndo: (patch) => {
          applyPatch(patch);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? { ...msg, pending: false, canUndo: true }
                : msg
            )
          );
        },
        onPreview: (preview) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId
                ? { ...msg, pending: false, preview, previewState: 'ready' }
                : msg
            )
          );
        },
      });
    } catch (err) {
      const message = err?.message || 'Brain request failed.';
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantId
            ? { ...msg, text: message, pending: false, error: true }
            : msg
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault?.();
    sendText(draft);
  };

  return (
    <>
      <aside
        ref={panelRef}
        className={`brain-panel ${isResizing ? 'brain-panel--resizing' : ''}`}
        style={{ width: brainWidth }}
        role="complementary"
        aria-label="Brain"
      >
        <header className="brain-panel__header">
          <div className="brain-panel__title-block">
            <span className="brain-panel__mark" aria-hidden />
            <div>
              <h2 className="brain-panel__title">Brain</h2>
              <p className="brain-panel__subtitle">Pipe test · model comes next</p>
            </div>
          </div>
          <div className="brain-panel__header-actions">
            {canViewMemory && (
              <button
                type="button"
                className="brain-panel__link-btn"
                onClick={onOpenMemory}
              >
                Memory
              </button>
            )}
            <button
              type="button"
              className="brain-panel__icon-btn"
              onClick={onClose}
              title="Close Brain"
              aria-label="Close Brain"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </header>

        <div className="brain-panel__memories">
          <div className="brain-panel__section-label">
            <span>Recent memories</span>
          </div>
          <ul className="brain-panel__memory-list">
            {STUB_MEMORIES.map((m) => (
              <li key={m.id} className="brain-panel__memory">
                <p className="brain-panel__memory-text">{m.text}</p>
                <span className="brain-panel__memory-meta">{m.meta}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="brain-panel__chat" ref={listRef}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`brain-panel__bubble brain-panel__bubble--${msg.role}${
                msg.error ? ' brain-panel__bubble--error' : ''
              }${msg.pending ? ' brain-panel__bubble--pending' : ''}`}
            >
              {msg.chips?.length || msg.canUndo ? (
                <div className="brain-panel__chips">
                  {(msg.chips || []).map((chip) => (
                    <span key={chip} className="brain-panel__chip">
                      {chip}
                    </span>
                  ))}
                  {msg.canUndo ? (
                    <button
                      type="button"
                      className="brain-panel__chip brain-panel__chip--undo"
                      onClick={() => {
                        undo();
                        setMessages((prev) =>
                          prev.map((item) =>
                            item.id === msg.id ? { ...item, canUndo: false } : item
                          )
                        );
                      }}
                    >
                      Undo
                    </button>
                  ) : null}
                </div>
              ) : null}
              {msg.pending && !msg.text && !msg.ask && !msg.preview ? '…' : msg.text}
              {msg.preview ? (
                <div className="brain-panel__preview">
                  <p className="brain-panel__preview-sentence">{msg.preview.sentence}</p>
                  <ul className="brain-panel__preview-nodes">
                    {(msg.preview.nodes || []).map((node) => (
                      <li key={`${node.index}-${node.name}`} className="brain-panel__preview-node">
                        <span className="brain-panel__preview-node-name">
                          {node.kind === 'rename' && node.beforeName
                            ? `${node.beforeName} → ${node.name}`
                            : node.name}
                        </span>
                        {Array.isArray(node.dependsOn) && node.dependsOn.length ? (
                          <span className="brain-panel__preview-node-dep">
                            after {(node.dependsOn || [])
                              .map((idx) => msg.preview.nodes?.[idx]?.name || `#${idx}`)
                              .join(', ')}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {msg.previewState === 'ready' ? (
                    <div className="brain-panel__preview-actions">
                      <button
                        type="button"
                        className="brain-panel__preview-apply"
                        disabled={busy}
                        onClick={async () => {
                          const ok = await applyClipboard(msg.preview);
                          if (ok && msg.preview?.sentence) {
                            try {
                              await confirmBrainApply(workspaceId, msg.preview.diary || msg.preview.sentence);
                            } catch {
                              /* canvas already landed */
                            }
                          }
                          setMessages((prev) =>
                            prev.map((item) =>
                              item.id === msg.id
                                ? { ...item, previewState: ok ? 'applied' : 'blocked' }
                                : item
                            )
                          );
                        }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        className="brain-panel__preview-dismiss"
                        disabled={busy}
                        onClick={() => {
                          setMessages((prev) =>
                            prev.map((item) =>
                              item.id === msg.id ? { ...item, previewState: 'dismissed' } : item
                            )
                          );
                        }}
                      >
                        Dismiss
                      </button>
                    </div>
                  ) : null}
                  {msg.previewState === 'applied' ? (
                    <p className="brain-panel__preview-status">On the canvas. Undo from the campaign if you want it back.</p>
                  ) : null}
                  {msg.previewState === 'blocked' ? (
                    <p className="brain-panel__preview-status">Open that campaign, then Apply.</p>
                  ) : null}
                </div>
              ) : null}
              {msg.ask ? (
                <div className="brain-panel__ask">
                  <p className="brain-panel__ask-q">{msg.ask}</p>
                  {!msg.askAnswered ? (
                    <form
                      className="brain-panel__ask-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        sendText(askReply);
                      }}
                    >
                      <input
                        className="brain-panel__ask-input"
                        value={askReply}
                        onChange={(e) => setAskReply(e.target.value)}
                        placeholder="Answer…"
                        disabled={busy}
                      />
                      <button
                        type="submit"
                        className="brain-panel__ask-send"
                        disabled={!askReply.trim() || busy}
                      >
                        Answer
                      </button>
                    </form>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <form className="brain-panel__composer" onSubmit={handleSend}>
          <textarea
            ref={inputRef}
            className="brain-panel__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask your brain…"
            rows={2}
            disabled={busy}
          />
          <button
            type="submit"
            className="brain-panel__send"
            disabled={!draft.trim() || busy}
          >
            Send
          </button>
        </form>
      </aside>

      <div
        className={`brain-panel__resize-handle ${isResizing ? 'brain-panel__resize-handle--active' : ''}`}
        onMouseDown={handleResizeStart}
        onDoubleClick={handleResizeReset}
        title="Drag to resize • Double-click to reset"
      />
    </>
  );
};

export default BrainPanel;
